/**
 * Settings Layout Route
 * Handles permission check, renders desktop sidebar + <Outlet />.
 * Mobile: sidebar and header are hidden via CSS — child routes handle mobile UI.
 */

import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useHasPermission, useUserPermissions } from '@/hooks/usePermissions'
import { REPORTS_SECTIONS, SECURITY_SECTIONS, DATA_SECTIONS, CALENDAR_SECTIONS, SYSTEM_SECTIONS, type SettingsSection, type SectionItem } from './settings-sections'
import styles from '@/pages/Settings.module.css'

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsLayout,
})

export function SettingsLayout() {
  const navigate = useNavigate()
  const { data: userPermissions, isLoading: permissionsLoading } = useUserPermissions()
  const canManageSettings = useHasPermission('settings', 'manage')
  // Managers with tags:manage see the Attributes link for tag management (#903).
  const canManageTags = useHasPermission('tags', 'manage')
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  // Authorization is driven purely by RBAC permissions (#436) — any `settings`
  // permission grants access to the section. The deprecated `users.role` enum is
  // no longer consulted. While permissions load we render nothing (no flash) and
  // only redirect once we know the viewer lacks access.
  const canViewSettings = userPermissions?.permissions?.some(
    (p) => p.resource === 'settings'
  ) ?? false

  useEffect(() => {
    if (!canViewSettings && !permissionsLoading) {
      navigate({ to: '/dashboard' })
    }
  }, [canViewSettings, permissionsLoading, navigate])

  if (!canViewSettings) return null

  // Derive active section from URL for sidebar highlighting
  const activeSection = (pathname.split('/')[2] ?? '') as SettingsSection | ''

  const renderSidebarGroup = (label: string, items: SectionItem[]) => {
    const visibleItems = items.filter(item =>
      !item.adminOnly || canManageSettings || (item.id === 'attributes' && canManageTags))
    if (visibleItems.length === 0) return null

    return (
      <div className={styles.sidebarGroup}>
        <span className={styles.sidebarGroupLabel}>{label}</span>
        {visibleItems.map(item => (
          <Link
            key={item.id}
            to="/settings/$section"
            params={{ section: item.id }}
            className={`${styles.sidebarItem} ${activeSection === item.id ? styles.active : ''}`}
          >
            <span className={styles.sidebarIcon}>{item.icon}</span>
            <span className={styles.sidebarItemLabel}>{item.label}</span>
            <span className={styles.sidebarChevron}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="9,18 15,12 9,6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Link>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.settingsPage}>
      {/* Desktop page header — hidden on mobile via CSS */}
      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.subtitle}>Manage your preferences and configuration</p>
      </div>

      <div className={styles.layout}>
        {/* Sidebar — desktop only (hidden on mobile via CSS) */}
        <nav className={styles.sidebar} aria-label="Settings navigation">
          {renderSidebarGroup('Reports', REPORTS_SECTIONS)}
          {renderSidebarGroup('Security', SECURITY_SECTIONS)}
          {renderSidebarGroup('Data', DATA_SECTIONS)}
      {renderSidebarGroup('Calendar', CALENDAR_SECTIONS)}
          {renderSidebarGroup('System', SYSTEM_SECTIONS)}
        </nav>

        {/* Child route renders here */}
        <div className={styles.content}>
          <Outlet />
        </div>
      </div>
    </div>
  )
}
